// 浏览器端解密链——忠实移植 keytail-core 的解锁路径，让 Web 应急端能解出
// 服务端下发的真实云端 vault（apply_cloud_wrap 产物）。
//
// 链：Argon2id(PIN, kek_kdf.salt) → HKDF-SHA256(salt=S_server, info="keytail-v2-pin-wrap")
//     = KEK → XChaCha20-Poly1305 解 pin_wrapped_vdk = VDK → 解 payload blob + 字段密文。
//
// 跨语言一致性由 test/crypto.spec.ts 对 Rust 夹具断言（cargo run --example web_fixture）。
//
// 安全：明文只在内存，关闭页面即随 JS 上下文销毁；不写 localStorage/不缓存。
import { argon2id } from '@noble/hashes/argon2'
import { hkdf } from '@noble/hashes/hkdf'
import { hmac } from '@noble/hashes/hmac'
import { sha256 } from '@noble/hashes/sha2'
import { xchacha20poly1305 } from '@noble/ciphers/chacha'

const PIN_WRAP_INFO = new TextEncoder().encode('keytail-v2-pin-wrap')
const KTV1_MAGIC = new Uint8Array([0x4b, 0x54, 0x56, 0x31]) // "KTV1"
const KEY_WRAP_ALG = 'xchacha20poly1305-keywrap-v1'
const NONCE_LEN = 24
const KEY_LEN = 32
/** 恢复码 base32 字母表（无填充，A-Z + 2-7），对标 crypto.rs:base32_no_padding。 */
const B32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'
const RECOVERY_CODE_LEN = 24

function b64decode(s: string): Uint8Array {
  const bin = atob(s)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

function b64encode(bytes: Uint8Array): string {
  let bin = ''
  // 分块拼接，避免 String.fromCharCode(...大数组) 爆栈。
  const CHUNK = 0x8000
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK))
  }
  return btoa(bin)
}

interface KdfParams {
  algorithm: string
  m_kib: number
  t_cost: number
  p_cost: number
  salt_b64: string
}
interface WrappedKey {
  algorithm: string
  ciphertextB64: string
}
interface KeyWrap {
  kek_kdf: KdfParams
  pin_wrapped_vdk: WrappedKey
  recovery_wrapped_vdk?: WrappedKey | null
  uses_server_secret: boolean
}
interface VaultMeta {
  id: string
  name: string
  schema_version: number
  key_wrap?: KeyWrap | null
}

/** 解析 KTV1 文件：magic(4) + schema(4 LE) + meta_len(4 LE) + meta_json + payload(nonce24||ct)。 */
function parseVaultBlob(blob: Uint8Array): { meta: VaultMeta; payload: Uint8Array } {
  if (blob.length < 12) throw new Error('vault blob 截断')
  const magic = String.fromCharCode(blob[0], blob[1], blob[2], blob[3])
  if (magic !== 'KTV1') throw new Error('vault 魔数无效')
  const dv = new DataView(blob.buffer, blob.byteOffset, blob.byteLength)
  const metaLen = dv.getUint32(8, true)
  if (blob.length < 12 + metaLen) throw new Error('vault meta 截断')
  const meta: VaultMeta = JSON.parse(new TextDecoder().decode(blob.subarray(12, 12 + metaLen)))
  return { meta, payload: blob.subarray(12 + metaLen) }
}

/**
 * KEK = HKDF(salt=S_server, ikm=Argon2id(PIN, salt), info)；无 S_server 时退化为 Argon2id 输出。
 * 对标 keytail-core `crypto::derive_pin_unwrap_key`。导出供 bootstrap / 改 PIN 复用。
 */
export function deriveKek(pin: string, kek: KdfParams, sServer: Uint8Array | null): Uint8Array {
  const argonOut = argon2id(new TextEncoder().encode(pin), b64decode(kek.salt_b64), {
    t: kek.t_cost,
    m: kek.m_kib,
    p: kek.p_cost,
    dkLen: 32,
    version: 0x13,
  })
  if (!sServer || sServer.length === 0) return argonOut
  return hkdf(sha256, argonOut, sServer, PIN_WRAP_INFO, 32)
}

/** XChaCha20-Poly1305 解密：输入 = nonce(24) || ciphertext+tag。 */
function aeadOpen(key: Uint8Array, nonceAndCt: Uint8Array): Uint8Array {
  const nonce = nonceAndCt.subarray(0, NONCE_LEN)
  const ct = nonceAndCt.subarray(NONCE_LEN)
  return xchacha20poly1305(key, nonce).decrypt(ct)
}

/** XChaCha20-Poly1305 加密：返回 nonce(24) || ciphertext+tag 的拼接字节。显式 nonce（测试向量用）。 */
function aeadSealWithNonce(key: Uint8Array, nonce: Uint8Array, plaintext: Uint8Array): Uint8Array {
  const ct = xchacha20poly1305(key, nonce).encrypt(plaintext)
  const out = new Uint8Array(NONCE_LEN + ct.length)
  out.set(nonce, 0)
  out.set(ct, NONCE_LEN)
  return out
}

function randomNonce(): Uint8Array {
  const n = new Uint8Array(NONCE_LEN)
  crypto.getRandomValues(n)
  return n
}

function toBytes(v: string | Uint8Array): Uint8Array {
  return typeof v === 'string' ? new TextEncoder().encode(v) : v
}

export interface OpenedVault {
  /** VDK：用于按需解字段密文（密码 / secret）。 */
  vdk: Uint8Array
  /** 解密后的 VaultPayload（accounts / secrets / ...）。 */
  payload: any
  /** 明文 meta（含 key_wrap/kdf_params/settings）——回写时 packVault 复用。 */
  meta: Record<string, unknown>
}

/** 用 PIN + S_server 解开云端 vault：派生 KEK → 解 VDK → 解 payload。 */
export function openVault(vaultBlobB64: string, pin: string, sServerB64: string): OpenedVault {
  const { meta, payload } = parseVaultBlob(b64decode(vaultBlobB64))
  const kw = meta.key_wrap
  if (!kw) throw new Error('该 vault 非 v2 分层包装，无法应急解密')
  const sServer = kw.uses_server_secret ? b64decode(sServerB64) : null
  const kek = deriveKek(pin, kw.kek_kdf, sServer)
  let vdk: Uint8Array
  try {
    vdk = unwrapKey(kek, kw.pin_wrapped_vdk)
  } catch {
    throw new Error('PIN 或 S_server 不正确')
  }
  const payloadObj = JSON.parse(new TextDecoder().decode(aeadOpen(vdk, payload)))
  return { vdk, payload: payloadObj, meta: meta as unknown as Record<string, unknown> }
}

/** 用 VDK 解一段字段密文（base64(nonce||ct)）为明文字符串。 */
export function openField(vdk: Uint8Array, ciphertextB64: string): string {
  return new TextDecoder().decode(aeadOpen(vdk, b64decode(ciphertextB64)))
}

/** 只解析整包的明文 meta（不解 payload）——改 PIN 后刷新内存 meta 用。 */
export function readMeta(vaultBlobB64: string): Record<string, unknown> {
  return parseVaultBlob(b64decode(vaultBlobB64)).meta as unknown as Record<string, unknown>
}

/**
 * 用已持有的 VDK 直接解出整包 payload（免再派生 KEK/Argon2）。
 * 同步合并 409 后重拉远端时用：同一 vault 的 VDK 不变，可快速解远端 payload。
 */
export function decryptPayload(vaultBlobB64: string, vdk: Uint8Array): any {
  const { payload } = parseVaultBlob(b64decode(vaultBlobB64))
  return JSON.parse(new TextDecoder().decode(aeadOpen(vdk, payload)))
}

/** 尽力清零内存中的密钥材料（关闭/锁定时调）。 */
export function zeroize(...buffers: (Uint8Array | null | undefined)[]): void {
  for (const b of buffers) if (b) b.fill(0)
}

/** 字节 → 标准 base64（供同步引擎把 packVault 产物转成上传体）。 */
export function bytesToBase64(bytes: Uint8Array): string {
  return b64encode(bytes)
}

// ─────────────────────────────────────────────────────────────────────────
// 加密回写基座（写方向）——忠实对标 keytail-core crypto.rs，让 Web 能编辑并回写
// 真实云端 vault。跨语言一致性由 test/crypto.spec.ts 对 Rust 夹具/反向 capstone 断言。
// ─────────────────────────────────────────────────────────────────────────

/** 字段级加密：随机 nonce → base64(nonce24 || XChaCha20Poly1305(key, pt))。对标 `crypto::seal_field`。 */
export function sealField(key: Uint8Array, plaintext: string | Uint8Array): string {
  return b64encode(aeadSealWithNonce(key, randomNonce(), toBytes(plaintext)))
}

/** 显式 nonce 的字段加密——仅用于确定性测试向量（生产用 sealField）。 */
export function sealFieldWithNonce(
  key: Uint8Array,
  nonce: Uint8Array,
  plaintext: string | Uint8Array,
): string {
  return b64encode(aeadSealWithNonce(key, nonce, toBytes(plaintext)))
}

/** 包装一把 32 字节密钥（VDK）：对标 `crypto::wrap_key`，algorithm 固定串。 */
export function wrapKey(wrappingKey: Uint8Array, rawKey32: Uint8Array): WrappedKey {
  if (rawKey32.length !== KEY_LEN) throw new Error('被包装密钥长度必须为 32')
  return { algorithm: KEY_WRAP_ALG, ciphertextB64: sealField(wrappingKey, rawKey32) }
}

/** 解包密钥：校验 algorithm → 解出 32 字节。对标 `crypto::unwrap_key`。 */
export function unwrapKey(wrappingKey: Uint8Array, wrapped: WrappedKey): Uint8Array {
  if (wrapped.algorithm !== KEY_WRAP_ALG) throw new Error(`不支持的 key wrap: ${wrapped.algorithm}`)
  const raw = aeadOpen(wrappingKey, b64decode(wrapped.ciphertextB64))
  if (raw.length !== KEY_LEN) throw new Error('解包密钥长度异常')
  return raw
}

/** 密码指纹（重复检测）：base64(HMAC-SHA256(vdk, password)[..16])。对标 `crypto::password_fingerprint`。 */
export function passwordFingerprint(vdk: Uint8Array, password: string): string {
  const full = hmac(sha256, vdk, new TextEncoder().encode(password))
  return b64encode(full.subarray(0, 16))
}

/** 重新加密整个 payload（save 时刷新）：返回 base64(nonce||ct(payload_json))。对标 `storage::reseal_payload`。 */
export function resealPayload(vdk: Uint8Array, payload: unknown): string {
  return sealField(vdk, JSON.stringify(payload))
}

/**
 * 打包 KTV1 文件字节：magic"KTV1" + schema u32LE + metaLen u32LE + meta_json(UTF-8) + payload(nonce||ct)。
 * 对标 `storage::VaultFile::write_to` / `export_backup`。`payloadBlobB64` = resealPayload 产物。
 * meta 须为完整 VaultFileMeta（含 kdf_params/created_at/settings/key_wrap），原样 JSON 序列化。
 */
export function packVault(meta: Record<string, unknown>, payloadBlobB64: string): Uint8Array {
  const metaBytes = new TextEncoder().encode(JSON.stringify(meta))
  const payloadBytes = b64decode(payloadBlobB64)
  const schema = Number(meta.schema_version ?? 2)
  const out = new Uint8Array(12 + metaBytes.length + payloadBytes.length)
  out.set(KTV1_MAGIC, 0)
  const dv = new DataView(out.buffer)
  dv.setUint32(4, schema, true)
  dv.setUint32(8, metaBytes.length, true)
  out.set(metaBytes, 12)
  out.set(payloadBytes, 12 + metaBytes.length)
  return out
}

function randomBytes(n: number): Uint8Array {
  const b = new Uint8Array(n)
  crypto.getRandomValues(b)
  return b
}

export interface RewrapOptions {
  /** 覆盖新 KEK 派生的 Argon2 参数（测试用低参数加速；默认沿用原 kek_kdf 的参数）。 */
  argon?: { m_kib: number; t_cost: number; p_cost: number }
}

/**
 * 改 PIN（云端 vault 专用，对标里程碑 ADR 决策 E）：旧 PIN 解出 VDK → 新 salt 重派生 KEK
 * → 用新 KEK 重新包装**同一 VDK** → 换 meta.key_wrap → 重打包。VDK 不变、payload 字段密文
 * 原样保留（O(1)，不触碰任何字段密文）。
 *
 * core `change_password` 对云端 vault（uses_server_secret=true）直接拒绝（见 vault.rs），
 * 故由 web 自行实现。旧/新 KEK 与 VDK 用后尽力清零；旧 PIN 错误时 unwrapKey 抛错、不产出新包。
 *
 * @returns 新整包 blob（base64），待 uploadVault 一次回写。
 */
export function rewrapVaultPin(
  vaultBlobB64: string,
  oldPin: string,
  newPin: string,
  sServerB64: string,
  opts: RewrapOptions = {},
): string {
  const { meta, payload } = parseVaultBlob(b64decode(vaultBlobB64))
  const kw = meta.key_wrap
  if (!kw) throw new Error('该 vault 非 v2 分层包装，无法改 PIN')
  const sServer = kw.uses_server_secret ? b64decode(sServerB64) : null

  // 旧 PIN 解出 VDK（错误 PIN → unwrapKey 抛错，不产出新包）。
  const oldKek = deriveKek(oldPin, kw.kek_kdf, sServer)
  let vdk: Uint8Array
  try {
    vdk = unwrapKey(oldKek, kw.pin_wrapped_vdk)
  } catch {
    zeroize(oldKek)
    throw new Error('旧主密码不正确')
  }

  // 新 salt 重派生 KEK，用新 KEK 重新包装同一 VDK。
  const newKdf: KdfParams = {
    algorithm: kw.kek_kdf.algorithm,
    m_kib: opts.argon?.m_kib ?? kw.kek_kdf.m_kib,
    t_cost: opts.argon?.t_cost ?? kw.kek_kdf.t_cost,
    p_cost: opts.argon?.p_cost ?? kw.kek_kdf.p_cost,
    salt_b64: b64encode(randomBytes(16)),
  }
  const newKek = deriveKek(newPin, newKdf, sServer)
  const newWrap: KeyWrap = {
    kek_kdf: newKdf,
    pin_wrapped_vdk: wrapKey(newKek, vdk),
    recovery_wrapped_vdk: kw.recovery_wrapped_vdk ?? null,
    uses_server_secret: kw.uses_server_secret,
  }
  // 运行期 meta 含 settings/kdf_params/created_at 等全部字段（JSON.parse 保真）；
  // 展开只换 key_wrap，其余原样序列化回包。
  const newMeta = { ...(meta as unknown as Record<string, unknown>), key_wrap: newWrap }

  // VDK 不变 → payload 字段密文原样保留（仅换 meta，不重封 payload）。
  const out = packVault(newMeta, b64encode(payload))
  zeroize(oldKek, newKek, vdk)
  return b64encode(out)
}

// ─── 恢复码（对标 crypto.rs:269-327）────────────────────────────────────────

/** 15 随机字节 → base32 无填充(24 字符) → 每 4 字符以 '-' 分组。对标 `generate_recovery_code`。 */
export function generateRecoveryCode(): string {
  const raw = new Uint8Array(15)
  crypto.getRandomValues(raw)
  const enc = base32NoPadding(raw)
  const groups: string[] = []
  for (let i = 0; i < enc.length; i += 4) groups.push(enc.slice(i, i + 4))
  return groups.join('-')
}

/** 规范化：去空白/连字符、转大写、校验 24 位 base32(A-Z,2-7)。对标 `normalize_recovery_code`。 */
export function normalizeRecoveryCode(code: string): string {
  const normalized = code
    .split('')
    .filter((c) => !/\s/.test(c) && c !== '-')
    .join('')
    .toUpperCase()
  if (normalized.length !== RECOVERY_CODE_LEN) throw new Error('恢复码应为 24 位 base32 字符')
  if (!/^[A-Z2-7]+$/.test(normalized)) throw new Error('恢复码只能包含 A-Z 与 2-7')
  return normalized
}

/** 恢复码指纹 = hex(SHA256("keytail-recovery-code-v1" ‖ salt? ‖ 规范化码))。对标 `recovery_code_fingerprint`。 */
export function recoveryCodeFingerprint(code: string, salt: Uint8Array | null): string {
  const normalized = normalizeRecoveryCode(code)
  const domain = new TextEncoder().encode('keytail-recovery-code-v1')
  const codeBytes = new TextEncoder().encode(normalized)
  const saltBytes = salt ?? new Uint8Array(0)
  const buf = new Uint8Array(domain.length + saltBytes.length + codeBytes.length)
  buf.set(domain, 0)
  buf.set(saltBytes, domain.length)
  buf.set(codeBytes, domain.length + saltBytes.length)
  return toHex(sha256(buf))
}

function base32NoPadding(input: Uint8Array): string {
  let out = ''
  let buffer = 0
  let bitsLeft = 0
  for (const byte of input) {
    buffer = (buffer << 8) | byte
    bitsLeft += 8
    while (bitsLeft >= 5) {
      const idx = (buffer >> (bitsLeft - 5)) & 0b11111
      out += B32_ALPHABET[idx]
      bitsLeft -= 5
    }
  }
  if (bitsLeft > 0) {
    const idx = (buffer << (5 - bitsLeft)) & 0b11111
    out += B32_ALPHABET[idx]
  }
  return out
}

function toHex(bytes: Uint8Array): string {
  let s = ''
  for (const b of bytes) s += b.toString(16).padStart(2, '0')
  return s
}
